import { AbsoluteCenter } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import { Box, Typography, Show } from 'shared';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import { Nullable } from 'shared_DEPRECATED/types';
import { dateUtils } from 'shared_DEPRECATED/utils';

import {
  ParticipantsTableActionDisabled,
  ParticipantsTableActionWrapper,
  ParticipantsTableCounter,
} from 'features/participants/components/Table/Actions/';
import {
  ParticipantsTableLatestSprint,
  ParticipantsTableUserInfo,
} from 'features/participants/config/types';
import { useChatMessagesCountChange } from 'features/participants/hooks';
import { useLoadFirstUnreadMessage } from 'features/participants/hooks/useLoadFirstUnreadMessageTime';
import { getParticipantUrl } from 'features/participants/utils';

import { ButtonIcon } from 'shared/components/Button';

type ParticipantsTableMessagesPropTypes = {
  userInfo: ParticipantsTableUserInfo;
  latestSprint: ParticipantsTableLatestSprint;
  chatId: Nullable<string>;
  unreadChatMessagesNumber: number;
  lastReadMessageId: string;
  isChatMarkedAsUnread: boolean;
  isChatOpen: boolean;
  onChatOpen?: (email: string) => void;
};

export const ParticipantsTableMessages = ({
  userInfo,
  latestSprint,
  chatId,
  unreadChatMessagesNumber,
  lastReadMessageId,
  isChatMarkedAsUnread,
  onChatOpen,
  isChatOpen,
}: ParticipantsTableMessagesPropTypes) => {
  const { email: userEmail } = userInfo;

  const navigate = useNavigate();
  const userLink = getParticipantUrl(userInfo.email);

  useChatMessagesCountChange({
    chatId,
    userEmail,
    isChatOpen,
  });

  const hasUnreadMessages = unreadChatMessagesNumber > 0;

  const firstUnreadMessage = useLoadFirstUnreadMessage({
    chatId: chatId!,
    unreadChatMessagesNumber,
    lastReadMessageId,
  });

  const firstUnreadMessageTime = firstUnreadMessage?.createdAt
    ? dateUtils.calculateRelativeTimeAgo(firstUnreadMessage.createdAt)
    : '';

  const handleMessageClick = () => {
    // If onChatOpen callback is provided, use it to open chat on current page
    if (onChatOpen) {
      onChatOpen(userEmail);
      return;
    }

    // Otherwise, navigate to participant page with chat open (original behavior)
    const chatState = {
      openChat: true,
    };

    if (!latestSprint?.sprintId) {
      navigate(`/coach/participants/${userEmail}/dashboard`, {
        state: chatState,
      });
    }

    navigate(userLink, { state: chatState });
  };

  const isTimeAgoBadgeVisible =
    (hasUnreadMessages && firstUnreadMessageTime) ||
    (!hasUnreadMessages && isChatMarkedAsUnread);

  return chatId ? (
    <ButtonIcon
      aria-label="open chat"
      onClick={handleMessageClick}
      size="sm"
      variant="ghost"
      data-testid="participant-messages-btn"
    >
      <ParticipantsTableActionWrapper>
        <Box position="relative">
          <Show when={hasUnreadMessages}>
            <AbsoluteCenter insetY="45%">
              <Typography
                lineHeight="20px"
                textAlign="center"
                type="xsm"
                dataTestid="unread-messages-counter"
                color="black"
              >
                {unreadChatMessagesNumber}
              </Typography>
            </AbsoluteCenter>
          </Show>
          <Icon
            name={iconNames.chat}
            stroke="var(--fgPrimary)"
            width="24px"
            height="24px"
          />
          <Show when={isTimeAgoBadgeVisible}>
            <ParticipantsTableCounter
              dataTestid="unread-chat-badge"
              value={firstUnreadMessageTime}
            />
          </Show>
        </Box>
      </ParticipantsTableActionWrapper>
    </ButtonIcon>
  ) : (
    <ParticipantsTableActionDisabled
      icon={iconNames.chat}
      label="User has an inactive status"
    />
  );
};
